import React, { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Button } from 'components/ui/button';
import { Row, Col, Modal } from 'react-bootstrap-5';
import { faMailbox, faSearch } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { InputGroup, Form } from 'react-bootstrap-5';
import { debounce } from 'lodash';
import { faPenToSquare } from '@fortawesome/pro-solid-svg-icons';

import CallListExport from '../call_lists/export';
import AddProperty from '../call_lists/add_property';
import {
  fetchCallListItems,
  getCallListItems,
  mailersTemplatesUrl
} from 'bundles/connect/helpers/prospect_api';
import type { CallList, CallListItem, DataExportTemplate } from 'bundles/connect/types';
import CallListDetails from './call_list_details';
import type { CallListUser } from './manage_call_list_users';
import { setCallListItems } from '../store/actions/results';
import { Tooltip } from '@buildoutinc/blueprint-react/ui/Tooltip';

type CallListItemsHeaderProps = {
  allRestricted: boolean;
  callList: CallList;
  callListName: string;
  callListUsers: CallListUser[];
  templates: DataExportTemplate[];
  exportDisabled: string;
  setCallListName: React.Dispatch<React.SetStateAction<string>>;
  setPage: React.Dispatch<React.SetStateAction<number>>;
}


export default function CallListItemsTableHeader({
  allRestricted,
  callList,
  callListUsers,
  callListName,
  templates,
  exportDisabled,
  setCallListName,
  setPage
}: CallListItemsHeaderProps) {
  const dispatch = useDispatch();
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  const search = (query: string) => {
    const params = {
      'q[call_listable_of_Connect::Property_type_address_cont]': query
    };
    if (query === '') {
      getCallListItems({ page: 1 }, callList.id).then((r: { total: number, callListItems: CallListItem[] }) => {
        dispatch(setCallListItems(r.callListItems));
      });
    } else {
      fetchCallListItems(params, callList.id).then((data) => {
        dispatch(setCallListItems(data.data));
      });
    }
  };

  const debouncedSearch = useCallback(
    debounce((query) => {
      search(query);
    }, 300),
    []
  );

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPage(1);
    debouncedSearch(event.target.value);
  };

  return (
    <Row className="align-items-center gap-2">
      <Col className="me-auto" lg={6} xs={12} xxl={4}>
        <InputGroup className="flex-nowrap">
          <InputGroup.Text><FontAwesomeIcon icon={faSearch} /></InputGroup.Text>
          <Form.Control
            id="call_list_search"
            placeholder="Search for address"
            onChange={handleChange}
          />
        </InputGroup>
      </Col>
      <Col className="col-auto">
        <div className="d-flex align-items-center gap-2">
          {allRestricted ? (
            <Tooltip.Provider>
              <Tooltip>
                <Tooltip.Trigger render={<div />}>
                  <Button
                    disabled
                    href={mailersTemplatesUrl(callList.id)}
                    icon={faMailbox}
                    label="Create Mailers"
                    variant="ghost"
                  />
                </Tooltip.Trigger>
                <Tooltip.Content>All properties in this list are out of your selected counties</Tooltip.Content>
              </Tooltip>
            </Tooltip.Provider>
          ) : (
            <Button href={mailersTemplatesUrl(callList.id)} icon={faMailbox} label="Create Mailers" variant="ghost" />
          )}
          <CallListExport
            callListId={callList.id}
            disabled={exportDisabled}
            templates={templates}
          />
          <AddProperty callListId={callList.id} />
          <Button icon={faPenToSquare} label="Edit List" variant="ghost" onClick={() => setShowDetailsModal(true)} />
          <Modal centered show={showDetailsModal} onHide={() => setShowDetailsModal(false)}>
            <Modal.Header className="flex-row" closeButton>
              <Modal.Title as="h5">Details</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <CallListDetails
                callList={callList}
                callListName={callListName}
                callListUsers={callListUsers}
                setCallListName={setCallListName}
                onSubmit={() => setShowDetailsModal(false)}
              />
            </Modal.Body>
          </Modal>
        </div>
      </Col>
    </Row>
  );
}
